/**
 * @module app.cm
 */

import { autoinject, TaskQueue, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, IForm, SortDirection } from 'app/shared/lib/essette-site';
import { CarePlanService } from 'app/cm/services/care-plan-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { ExpandEvent, EssTableCustomElement } from 'app/shared/elements/ess-table';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { Toaster } from 'app/shared/lib/toaster';
import { ITasksConfig } from 'app/core/models/essette-interfaces';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { HouseholdCasePageContext } from 'app/cm/lib/household-case-page-context';
import { IApiSettings, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { sleep } from 'app/shared/lib/utilities';
import { I18N } from 'aurelia-i18n';
import { validate } from 'app/shared/elements/ess-rule';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IPrintable } from 'app/shared/lib/printing';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { AddGoals } from 'app/cm/components/household/care-plan/add-goals';
import { replaceCopyrightText } from 'app/ext/mcg-informed/lib/utilities';
import * as CM from 'app/cm/models/essette-api';

const expandedGoalsCache = new Map<string, Array<CM.CarePlanGoal>>();

@autoinject()
export class CarePlan extends ComponentBase implements IPrintable {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected householdCaseId: string | null = null;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected readonly: boolean = false;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected rememberExpand: boolean = true;

    protected goals: ReadonlyArray<CM.CarePlanGoal> = [];
    protected goalOption: string = 'selectGoals';
    protected mcgCopyright: string | null = null;

    protected hasAddGoalPermission: boolean = false;

    protected showMemberSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;
    protected showCaseSummaryLink: boolean = false;
    protected hasCaseRoute = false;
    protected openCaseSummary = openCaseSummary;
    protected caseSettings: IApiSettings = {};
    protected loadMcgGoalModal: boolean = false;
    /** Permission that controls whether the MCG Informed integration is available */
    protected hasMcgInformed: boolean = false;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    protected tasksConfig: ITasksConfig = {
        serviceRootUrl: 'cm/careplans',
        targetType: 'CM.CarePlanGoal',
        primaryTargetType: 'CM.CarePlanIntervention',
        labelKey: 'cm:carePlanIntervention.$',
        allowDuplicates: false
    };

    protected validate = validate;
    protected isValid: boolean = false;

    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<CM.CarePlanGoal[]>;
    protected $pager!: EssPagerCustomElement;
    protected $goalModal!: IPopup;
    protected $goalForm!: IForm;
    protected $mcgGoalModal!: IPopup;
    protected $mcgGoalForm!: IForm;
    protected $addGoals!: AddGoals;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CarePlanService,
        private readonly queue: TaskQueue,
        private readonly toaster: Toaster,
        private context: HouseholdCasePageContext,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        this.checkPermissions();

        this.caseSettings = this.context.settings;
        this.tasksConfig.allowDuplicates = !!getApiSetting(this.caseSettings, 'carePlan.goals.interventions.$allowDuplicates');

        // Set generalized copyright text
        const copyright = getApiSetting(this.caseSettings, 'mcg.informed.copyright.$display')?.toString();
        const edition = getApiSetting(this.caseSettings, 'mcg.informed.edition.$display')?.toString();
        if(copyright) this.mcgCopyright = replaceCopyrightText(copyright, edition);

        this.reload();
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected formSuccess({ detail }: FormSuccessCustomEvent): void {
        const goalsCount = this.$addGoals.goalsCount;
        this.toaster.standard.saved('cm:carePlanGoal.$', true, goalsCount);
        this.$goalModal.close();

        if(detail.form && detail.form.reset) {
            detail.form.reset();
        }

        this.reload();
    }

    protected formSuccessMcg({ detail }: FormSuccessCustomEvent): void {
        this.toaster.standard.saved('ext:cci.goal.$', true);
        this.$mcgGoalModal.close();

        if(detail.form && detail.form.reset) {
            detail.form.reset();
        }

        this.reload();

        this.loadMcgGoalModal = false;
    }

    ////****************** Functions ************************************************////
    public async prepareForPrinting(): Promise<void> {
        // expand all goals
        for(let index = 0; index < this.goals.length; index++) {
            this.$table.open(index);
        }
        // this may not be needed, but it won't be noticeable
        // this will make sure all the elements get updated before the call to window.print
        await sleep(250);
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.hasAddGoalPermission = this.accountContext.hasPermission('CM.CarePlanGoal.Insert.Add');
        this.hasMcgInformed = this.env.config.integrations.includes('cci') && this.accountContext.hasRouteAccess('cci.assessment');

        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
    }

    public async reload(): Promise<void> {
        await this.loadCarePlan();
    }

    protected newGoal(): void {
        this.$goalModal.open();
    }

    protected goalModalClose(): void {
        if(this.$goalForm.reset) {
            this.$goalForm.reset();
        }
        this.$goalModal.close();
    }

    protected newMcgGoal(): void {
        this.loadMcgGoalModal = true;
        this.$mcgGoalModal.open();
    }

    protected mcgGoalModalClose(): void {
        console.log('mcgGoalModalClose');
        if(this.$mcgGoalForm.reset) {
            console.log('$mcgGoalForm.reset');
            this.$mcgGoalForm.reset();
        }
        console.log('$mcgGoalModal.close');
        this.$mcgGoalModal.close();
    }

    private restoreExpanded() {
        if(!this.rememberExpand) { return; }
        // we need to give aurelia a chance to bind the goals into the
        // table before we ask the table to expand the rows
        this.queue.queueTask(() => {
            if(!this.householdCaseId) { return; }
            const expandedGoals = expandedGoalsCache.get(this.householdCaseId);
            if(expandedGoals !== undefined) {
                this.$table.restoreExpanded(expandedGoals);
            }
        });
    }

    protected trackExpanded({ detail }: ExpandEvent<CM.CarePlanGoal>) {
        if(!this.rememberExpand || !this.householdCaseId) { return; }
        expandedGoalsCache.set(this.householdCaseId, detail.items);
    }

    protected buildCreatedByMessage(goal: CM.CarePlanGoal): string | null {
        const stamp = goal.metadata?.created?.timestamp;
        const byUser = goal.metadata?.created?.byUser?.description;


        if(goal.metadata?.triggeredBy?.description) {
            const key = 'ui:stamps.triggeredBy.timestamp'
            const source = goal.metadata.triggeredBy.description;
            return this.i18n.tr(key, { source: source, timestamp: stamp, user: byUser });
        }
        else {
            const key = 'ui:stamps.created.timestamp'
            return this.i18n.tr(key, { timestamp: stamp, user: byUser });
        }
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadCarePlan(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes: string[] = [
            'case',
            'case.member',
            'priority',
            'externalSystem',
            'interventions.definition.type',
            'interventions.delegated.queue',
            'interventions.delegated.to',
            'interventions.externalSystem',
            'interventions.summary',
            'interventions.goal',
            'interventions.metadata.created.byUser',
            'interventions.metadata.completed.byUser',
            'interventions.metadata.delegated.byUser',
            'interventions.metadata.dueDateTimeSet.byUser',
            'barriers.metadata.created.byUser',
            'outcomes.metadata.created.byUser',
            'metadata.completed.byUser',
            'metadata.created.byUser',
            'metadata.targetDateSet.byUser'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadHouseholdCaseCarePlan(this.householdCaseId, options, includes);

            this.goals = response?.results;
            this.resultCount = response?.count || 0;
            this.restoreExpanded();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
